import { jsPDF } from "jspdf";

const GREEN = [16, 185, 129];
const CYAN = [6, 182, 212];
const AMBER = [245, 158, 11];
const DARK = [15, 23, 42];
const GREY = [100, 116, 139];

const tierColor = (s) => {
  if (s >= 90) return GREEN;
  if (s >= 75) return CYAN;
  if (s >= 60) return AMBER;
  return [239, 68, 68];
};

export const generateReportPdf = (assessment) => {
  const { report } = assessment;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 44;
  let y = M;

  const ensure = (need) => {
    if (y + need > H - M) {
      doc.addPage();
      y = M;
    }
  };
  const line = (x1, yy, x2) => {
    doc.setDrawColor(226, 232, 240);
    doc.line(x1, yy, x2, yy);
  };

  // Header
  doc.setFillColor(11, 15, 18);
  doc.rect(0, 0, W, 74, "F");
  doc.setTextColor(248, 250, 252);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("CreaseAI", M, 34);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(148, 163, 184);
  doc.text("NET BIOMECHANICS  ·  POWERED BY REZZIL", M, 50);
  doc.setFontSize(9);
  doc.setTextColor(148, 163, 184);
  const dateStr = new Date(assessment.created_at).toLocaleString();
  doc.text(dateStr, W - M, 34, { align: "right" });
  y = 100;

  // Batter + meta
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(assessment.batter_name || "Net Batter", M, y);
  y += 18;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...GREY);
  const meta = [assessment.camera_angle, assessment.handedness, report.detected_shot_type || assessment.shot_type]
    .filter(Boolean)
    .join("   ·   ");
  doc.text(meta, M, y);
  y += 22;

  // Overall score
  const oc = tierColor(assessment.overall_score);
  doc.setFillColor(...oc);
  doc.roundedRect(M, y, 92, 54, 8, 8, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.text(String(assessment.overall_score), M + 46, y + 32, { align: "center" });
  doc.setFontSize(7);
  doc.text("NET EFFICIENCY / 100", M + 46, y + 46, { align: "center" });

  doc.setTextColor(...DARK);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const sum = doc.splitTextToSize(report.summary || "", W - M - (M + 108));
  doc.text(sum, M + 108, y + 14);
  y += 70;

  // Ball metrics
  ensure(50);
  line(M, y, W - M);
  y += 16;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...DARK);
  doc.text("Ball Speed & Shot Distance", M, y);
  y += 15;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const bm = report.ball_metrics || {};
  if (bm.can_estimate && (bm.shot_speed_kmh != null || bm.shot_distance_m != null)) {
    doc.setTextColor(...DARK);
    const spd = bm.shot_speed_kmh != null ? `${Math.round(bm.shot_speed_kmh)} km/h` : "—";
    const dist = bm.shot_distance_m != null ? `${Math.round(bm.shot_distance_m * 10) / 10} m` : "—";
    doc.text(`Est. shot speed: ${spd}      Est. shot distance: ${dist}      (${(bm.confidence || "").toUpperCase()} confidence)`, M, y);
    y += 14;
    if (bm.assumptions) {
      doc.setTextColor(...GREY);
      doc.setFontSize(9);
      doc.text(doc.splitTextToSize(bm.assumptions, W - 2 * M), M, y);
      y += 12;
    }
  } else {
    doc.setTextColor(...GREY);
    const msg = "Speed and distance assessment isn't available due to video quality. " + (bm.reason || "");
    doc.text(doc.splitTextToSize(msg, W - 2 * M), M, y);
    y += 20;
  }
  y += 8;

  // Pillars
  (report.pillars || []).forEach((p) => {
    ensure(90);
    line(M, y, W - M);
    y += 16;
    const pc = tierColor(p.score);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...DARK);
    doc.text(p.title, M, y);
    doc.setTextColor(...pc);
    doc.text(`${p.score}/100`, W - M, y, { align: "right" });
    y += 8;
    // score bar
    doc.setFillColor(226, 232, 240);
    doc.roundedRect(M, y, W - 2 * M, 6, 3, 3, "F");
    doc.setFillColor(...pc);
    doc.roundedRect(M, y, (W - 2 * M) * (p.score / 100), 6, 3, 3, "F");
    y += 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...DARK);
    const asr = doc.splitTextToSize(p.assessment || "", W - 2 * M);
    doc.text(asr, M, y);
    y += asr.length * 12 + 4;
    (p.metrics || []).forEach((m) => {
      ensure(16);
      const rc = (m.rating || "").toLowerCase() === "good" ? GREEN : (m.rating || "").toLowerCase() === "fair" ? AMBER : [239, 68, 68];
      doc.setFillColor(...rc);
      doc.circle(M + 4, y - 3, 2.5, "F");
      doc.setTextColor(...DARK);
      doc.setFontSize(9.5);
      const txt = `${m.label}  —  ${(m.rating || "").toUpperCase()}${m.note ? ": " + m.note : ""}`;
      const tl = doc.splitTextToSize(txt, W - 2 * M - 16);
      doc.text(tl, M + 14, y);
      y += tl.length * 12 + 2;
    });
    y += 6;
  });

  // Strengths / improvements / drills
  const bulletBlock = (title, items, color) => {
    if (!items || !items.length) return;
    ensure(40);
    line(M, y, W - M);
    y += 16;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...color);
    doc.text(title, M, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...DARK);
    items.forEach((it) => {
      const t = typeof it === "string" ? it : `${it.title}: ${it.description}`;
      const tl = doc.splitTextToSize("•  " + t, W - 2 * M);
      ensure(tl.length * 12 + 4);
      doc.text(tl, M, y);
      y += tl.length * 12 + 4;
    });
    y += 6;
  };

  bulletBlock("Strengths", report.strengths, GREEN);
  bulletBlock("Areas to Improve", report.improvements, AMBER);
  bulletBlock("Recommended Drills", report.drills, CYAN);

  const safe = (assessment.batter_name || "batter").replace(/[^a-z0-9]/gi, "_");
  doc.save(`CreaseAI_${safe}_${assessment.overall_score}.pdf`);
};
